import { useEffect, useState } from "react";
import { InitiatePaymentUseCase } from "../../../../domain/usecase/payment/initiate/UseCase";
import { useSnackbar } from "notistack";
import { PaymentMethod } from "../../../../domain/model/Payment";
import { State } from "../../../../domain/model/ResponseState";
import { Package } from "../../../../domain/model/Package";
import { ExecutePaymentUseCase } from "../../../../domain/usecase/payment/execute/UseCase";
import { useTranslation } from "react-i18next";
import { Player } from "../../../../domain/model/User";
import { CheckDiscountCodeIfRedeemableUseCase } from "../../../../domain/usecase/discountCode/checkIfRedeemable/UseCase";
import { DiscountCodeType } from "../../../../domain/model/DiscountCode";
import { openLink, showGlobalLoadingView } from "../../../../core/utils/Utils";

export function PaymentViewModel(
  packageItem: Package,
  player: Player,
  initiatePaymentUseCase: InitiatePaymentUseCase,
  executePaymentUseCase: ExecutePaymentUseCase,
  checkDiscountCodeIfRedeemableUseCase: CheckDiscountCodeIfRedeemableUseCase
) {
  const { enqueueSnackbar } = useSnackbar();
  const [discountCode, setDiscountCode] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<
    Array<PaymentMethod> | undefined
  >([]);

  const { i18n } = useTranslation();

  const isRtl = i18n.dir() == "rtl";

  const [discount, setDiscount] = useState<number>(0);
  const [discountCodeId, setDiscountCodeId] = useState<string | undefined>();

  useEffect(() => {
    initiatePayment();
  }, [discount]);

  function truncateToDecimalPlaces(
    value: number,
    fractionalDigits: number
  ): number {
    return (
      Math.trunc(value * Math.pow(10, fractionalDigits)) /
      Math.pow(10, fractionalDigits)
    );
  }

  function calculateDiscountedPriceInUSD(): number {
    return truncateToDecimalPlaces(
      (packageItem.priceInUSD! * (100 - discount)) / 100,
      2
    );
  }

  function calculateDiscountedPriceInQAR(): number {
    return truncateToDecimalPlaces(
      (packageItem.priceInQAR! * (100 - discount)) / 100,
      2
    );
  }

  async function initiatePayment() {
    setLoading(true);
    const response = await initiatePaymentUseCase.invoke(
      calculateDiscountedPriceInQAR()
    );
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        const paymentMethodsList = response.data?.filter(
          (item) => !item.IsDirectPayment
        );
        const reorderedPayments = [...paymentMethodsList]
        const newOrder = [3, 0, 2, 4, 5, 1];
        for (var i = 0; i < paymentMethodsList.length; i++) {
          reorderedPayments[i] = paymentMethodsList[newOrder[i]];
        }
        setPaymentMethods(
          reorderedPayments
        );
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setLoading(false);
  }

  async function checkIfDiscountCodeRedeemable() {
    if (!discountCode) return;
    showGlobalLoadingView(true);
    const response = await checkDiscountCodeIfRedeemableUseCase.invoke({
      type: DiscountCodeType.DISCOUNTED_PURCHASE,
      code: discountCode,
    });
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        if (
          response.data?.canBeUsed &&
          response.data.discountCodeId &&
          response.data.discount != undefined
        ) {
          setDiscountCodeId(response.data.discountCodeId);
          setDiscount(response.data.discount);
        } else {
          showSnackbar(response.data?.message);
        }
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function executePayment(paymentMethodId: number) {
    showGlobalLoadingView(true);
    const response = await executePaymentUseCase.invoke({
      discount,
      numberOfPurchasedGames: packageItem.numberOfGames!,
      originalPriceInQAR: packageItem.priceInQAR!,
      originalPriceInUSD: packageItem.priceInUSD!,
      language: isRtl ? "AR" : "EN",
      discountId: discountCodeId,
      invoiceValue: calculateDiscountedPriceInQAR(),
      paymentMethodId,
      userId: player.user?._id!,
      removeHashtag: true,
    });
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        const paymentUrlModel = response.data;
        if (!paymentUrlModel) return;
        if (paymentUrlModel.paymentURL.length == 0) {
          showSnackbar(paymentUrlModel.error!);
          return;
        }
        openLink(paymentUrlModel.paymentURL);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }
  return {
    paymentMethods,
    loading,
    discount,
    calculateDiscountedPriceInUSD,
    executePayment,
    discountCode,
    setDiscountCode,
    checkIfDiscountCodeRedeemable,
  };
}
